import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const FormularioEventoTurismo: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    titulo: '',
    tipo: '',
    descripcion: '',
    ubicacion: '',
    comunidad: '',
    fecha: '',
    horaInicio: '',
    horaFin: '',
    itinerario: '',
    responsable: '',
    artesanosIds: '',
    expositores: '',
    personal: '',
    presupuesto: '',
    compras: '',
    obsTesoreria: '',
    estatus: 'activo',
  });
  const [guardando, setGuardando] = useState(false);
  const [artesanos, setArtesanos] = useState<any[]>([]);
  const [cotizacionesExistentes, setCotizacionesExistentes] = useState<string[]>([]);
  const [cotizacionesNuevas, setCotizacionesNuevas] = useState<File[]>([]);

  useEffect(() => {
    axiosInstance.get('/turismo/artesanos').then((res) => setArtesanos(res.data || [])).catch(() => setArtesanos([]));
  }, []);

  useEffect(() => {
    if (data) {
      setForm({
        titulo: data.titulo || '',
        tipo: data.tipo || '',
        descripcion: data.descripcion || '',
        ubicacion: data.ubicacion || '',
        comunidad: data.comunidad || '',
        fecha: data.fecha ? data.fecha.split('T')[0] : '',
        horaInicio: data.horaInicio || '',
        horaFin: data.horaFin || '',
        itinerario: data.itinerario || '',
        responsable: data.responsable || '',
        artesanosIds: Array.isArray(data.artesanosIds) ? data.artesanosIds.map(String).join(', ') : data.artesanosIds || '',
        expositores: data.expositores || '',
        personal: data.personal || '',
        presupuesto: data.presupuesto || '',
        compras: data.compras || '',
        obsTesoreria: data.obsTesoreria || '',
        estatus: data.estatus || 'activo',
      });
      setCotizacionesExistentes(Array.isArray(data.cotizaciones) ? data.cotizaciones.filter(Boolean) : []);
      setCotizacionesNuevas([]);
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const selectedArtesanosIds = useMemo(
    () => (form.artesanosIds ? String(form.artesanosIds).split(',').map((v: string) => v.trim()).filter(Boolean) : []),
    [form.artesanosIds]
  );

  const handleArtesanosChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    handleChange('artesanosIds', values.join(', '));
  };

  const handleCotizaciones = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCotizacionesNuevas((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.titulo || !form.tipo || !form.fecha) return;
    setGuardando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'artesanosIds') return;
        fd.append(k, String(v ?? ''));
      });
      fd.append('artesanosIds', JSON.stringify(selectedArtesanosIds.map((id) => Number(id))));
      fd.append('cotizacionesExistentes', JSON.stringify(cotizacionesExistentes));
      cotizacionesNuevas.forEach((file) => fd.append('cotizaciones', file));

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/eventos/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/eventos', fd);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <Stack spacing={5}>
        <Box borderWidth="1px" borderRadius="lg" p={4} bg="#f7efe8">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Informacion del evento</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Titulo</FormLabel>
              <Input value={form.titulo} onChange={(e) => handleChange('titulo', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Tipo</FormLabel>
              <Input value={form.tipo} onChange={(e) => handleChange('tipo', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Ubicacion</FormLabel>
              <Input value={form.ubicacion} onChange={(e) => handleChange('ubicacion', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Comunidad</FormLabel>
              <Input value={form.comunidad} onChange={(e) => handleChange('comunidad', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Responsable</FormLabel>
              <Input value={form.responsable} onChange={(e) => handleChange('responsable', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Estatus</FormLabel>
              <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
                <option value="activo">Activo</option>
                <option value="concluido">Concluido</option>
              </Select>
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Descripcion</FormLabel>
              <Textarea value={form.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} isDisabled={disabled} rows={3} />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" color="#5b182b" mb={3}>Agenda y horario</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Fecha del evento</FormLabel>
              <Input type="date" value={form.fecha} onChange={(e) => handleChange('fecha', e.target.value)} isDisabled={disabled} />
              <FormHelperText>El sistema actual guarda una fecha principal.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Rango de horario</FormLabel>
              <HStack>
                <Input type="time" value={form.horaInicio} onChange={(e) => handleChange('horaInicio', e.target.value)} isDisabled={disabled} />
                <Text color="gray.500">a</Text>
                <Input type="time" value={form.horaFin} onChange={(e) => handleChange('horaFin', e.target.value)} isDisabled={disabled} />
              </HStack>
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Itinerario</FormLabel>
              <Textarea value={form.itinerario} onChange={(e) => handleChange('itinerario', e.target.value)} isDisabled={disabled} rows={3} />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Operacion y control</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Artesanos (seleccion multiple)</FormLabel>
              <Select multiple h="140px" value={selectedArtesanosIds} onChange={handleArtesanosChange} isDisabled={disabled}>
                {artesanos.map((a) => (
                  <option key={a.id} value={String(a.id)}>{a.nombres} {a.apellidos}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Expositores</FormLabel>
              <Input value={form.expositores} onChange={(e) => handleChange('expositores', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Personal</FormLabel>
              <Input value={form.personal} onChange={(e) => handleChange('personal', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Presupuesto</FormLabel>
              <Input type="number" value={form.presupuesto} onChange={(e) => handleChange('presupuesto', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Compras</FormLabel>
              <Textarea value={form.compras} onChange={(e) => handleChange('compras', e.target.value)} isDisabled={disabled} rows={3} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Observaciones de tesoreria</FormLabel>
              <Textarea value={form.obsTesoreria} onChange={(e) => handleChange('obsTesoreria', e.target.value)} isDisabled={disabled} rows={3} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Cotizaciones (archivo)</FormLabel>
              {!disabled ? (
                <>
                  <Input type="file" accept=".pdf,image/*" multiple onChange={handleCotizaciones} p={1} />
                  <FormHelperText>Sube cotizaciones en PDF o imagen, no URLs.</FormHelperText>
                </>
              ) : (
                <Text fontSize="sm" color="gray.500">Solo lectura</Text>
              )}
            </FormControl>
          </SimpleGrid>

          <Box mt={4}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Cotizaciones registradas</Text>
            {cotizacionesExistentes.length === 0 ? (
              <Text fontSize="sm" color="gray.500">Sin cotizaciones registradas.</Text>
            ) : (
              <Wrap>
                {cotizacionesExistentes.map((c) => (
                  <WrapItem key={c}>
                    <Tag colorScheme="red" borderRadius="full">
                      <TagLabel><Link href={resolveAssetUrl(c)} isExternal>{c.split('/').pop()}</Link></TagLabel>
                      {!disabled && <TagCloseButton onClick={() => setCotizacionesExistentes((prev) => prev.filter((x) => x !== c))} />}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>

          <Box mt={3}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Nuevas cotizaciones por subir</Text>
            {cotizacionesNuevas.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No has seleccionado archivos.</Text>
            ) : (
              <Wrap>
                {cotizacionesNuevas.map((f, i) => (
                  <WrapItem key={`${f.name}-${i}`}>
                    <Tag colorScheme="blue" borderRadius="full">
                      <TagLabel>{f.name}</TagLabel>
                      {!disabled && <TagCloseButton onClick={() => setCotizacionesNuevas((prev) => prev.filter((_, idx) => idx !== i))} />}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        </Box>
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioEventoTurismo;
